<script setup lang="ts">
import { computed } from 'vue'

import BetaStatusBadge from '@/components/beta-dashboard/atoms/BetaStatusBadge.vue'
import type { BetaActiveLayout, BetaTemplate } from '@/types/beta-dashboard'

const props = withDefaults(defineProps<{
    layout: BetaActiveLayout
    template?: BetaTemplate | null
    disabled?: boolean
}>(), {
    template: null,
    disabled: false,
})

const emit = defineEmits<{
    preview: [template: BetaTemplate]
}>()

const title = computed(() => (
    props.template?.description
    || props.layout.templateName
    || `Layout ${props.layout.templateId}`
))

const themeDescription = computed(() => {
    if (!props.template) return 'Modelo atualmente aplicado ao site'

    const mode = props.template.themeMode === 'dark' ? 'Tema escuro' : 'Tema claro'
    return props.template.theme ? `${mode} · ${props.template.theme}` : mode
})
</script>

<template>
    <article class="beta-active-layout">
        <div class="beta-active-layout__preview">
            <img
                v-if="props.template?.thumbnail"
                :src="props.template.thumbnail"
                :alt="`Prévia do layout ativo ${title}`"
            >
            <span v-else aria-hidden="true">Aa</span>
            <em>Em uso agora</em>
        </div>

        <div class="beta-active-layout__copy">
            <div class="beta-active-layout__eyebrow">
                <span>Layout ativo</span>
                <BetaStatusBadge label="Publicado" tone="success" />
            </div>
            <h3>{{ title }}</h3>
            <p>{{ themeDescription }}</p>
            <small>
                Este é o visual aplicado ao site. Escolha uma alternativa abaixo somente se quiser
                atualizar a aparência.
            </small>

            <button
                v-if="props.template?.url"
                type="button"
                :disabled="props.disabled"
                @click="emit('preview', props.template)"
            >
                Visualizar modelo <span aria-hidden="true">↗</span>
            </button>
        </div>
    </article>
</template>

<style scoped>
.beta-active-layout {
    display: grid;
    grid-template-columns: minmax(220px, 0.9fr) minmax(0, 1.1fr);
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--main-color) 32%, white);
    border-radius: 22px;
    background:
        radial-gradient(circle at 100% 0, color-mix(in srgb, var(--main-color) 11%, transparent), transparent 44%),
        #fff;
}

.beta-active-layout__preview {
    position: relative;
    display: grid;
    min-height: 220px;
    place-items: center;
    overflow: hidden;
    border-right: 1px solid #e5ebf2;
    background: #eef3f8;
    color: var(--main-color);
    font-size: 34px;
    font-weight: 850;
}

.beta-active-layout__preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
}

.beta-active-layout__preview em {
    position: absolute;
    inset: 12px auto auto 12px;
    border-radius: 999px;
    background: rgba(23, 33, 50, 0.88);
    padding: 6px 9px;
    color: #fff;
    font-size: 10px;
    font-style: normal;
    font-weight: 850;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    backdrop-filter: blur(8px);
}

.beta-active-layout__copy {
    display: flex;
    min-width: 0;
    align-items: flex-start;
    flex-direction: column;
    justify-content: center;
    padding: 24px;
}

.beta-active-layout__eyebrow {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.beta-active-layout__eyebrow > span {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.beta-active-layout h3 {
    margin-top: 13px;
    color: #172d4a;
    font-size: 14px;
    font-weight: 850;
    letter-spacing: -0.035em;
}

.beta-active-layout p {
    margin-top: 5px;
    color: #647891;
    font-size: 12px;
    font-weight: 700;
}

.beta-active-layout small {
    display: block;
    margin-top: 13px;
    color: #8493a7;
    font-size: 10px;
    line-height: 1.55;
}

.beta-active-layout button {
    margin-top: 18px;
    border: 1px solid #dce4ee;
    border-radius: var(--beta-button-radius);
    padding: 9px 12px;
    color: var(--main-color);
    font-size: 9px;
    font-weight: 850;
}

.beta-active-layout button:hover:not(:disabled),
.beta-active-layout button:focus-visible {
    border-color: color-mix(in srgb, var(--main-color) 28%, white);
    background: color-mix(in srgb, var(--main-color) 7%, white);
}

.beta-active-layout button:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--main-color) 20%, transparent);
    outline-offset: 2px;
}

.beta-active-layout button:disabled {
    cursor: wait;
    opacity: 0.5;
}

@media (max-width: 720px) {
    .beta-active-layout {
        grid-template-columns: 1fr;
    }

    .beta-active-layout__preview {
        min-height: 190px;
        aspect-ratio: 16 / 9;
        border-right: 0;
        border-bottom: 1px solid #e5ebf2;
    }

    .beta-active-layout__copy {
        padding: 20px;
    }
}
</style>
